import { startCase } from 'es-toolkit';
import React from 'react';
import { Box } from '@mui/material';

import {
  featurePatternMaterialSettingsProperties,
  Lumber,
  MaterialSettings,
  materialSettingsLumberProperties,
  profileMaterialSettingsProperties,
  uniqueFeaturePatternMaterialSettingsProperties,
  uniqueProfileMaterialSettingsProperties,
} from '@paragon/contract-library';
import { lumberCanBeUsedForComponentThickness } from '@paragon/material-lists';
import { assertExhaustiveSwitch, KeyToValueOfType } from '@paragon/utilities';

import LumberOption from './LumberOption';

type LumberPropertyKey = KeyToValueOfType<MaterialSettings, Lumber>;

export type MaterialSettingsLumbersMode = 'all' | 'featurePattern' | 'profile';

interface Props {
  disabled?: boolean;
  inventoryLumbers: Lumber[];
  componentThickness?: number;
  settings: MaterialSettings;
  onSettingsChange: (updatedSettings: MaterialSettings) => void;
  mode: MaterialSettingsLumbersMode;
  shareCommonMaterialSettingsFields?: boolean;
}

function getPropertiesToShow(
  mode: MaterialSettingsLumbersMode,
  shareCommonMaterialSettingsFields: boolean,
): readonly LumberPropertyKey[] {
  switch (mode) {
    case 'all':
      return materialSettingsLumberProperties;

    case 'featurePattern':
      return shareCommonMaterialSettingsFields
        ? uniqueFeaturePatternMaterialSettingsProperties
        : featurePatternMaterialSettingsProperties;

    case 'profile':
      return shareCommonMaterialSettingsFields
        ? uniqueProfileMaterialSettingsProperties
        : profileMaterialSettingsProperties;

    default:
      assertExhaustiveSwitch(mode);
  }
}

export default function MaterialSettingsLumbers({
  disabled,
  inventoryLumbers,
  componentThickness,
  settings,
  onSettingsChange,
  mode,
  shareCommonMaterialSettingsFields = false,
}: Props) {
  const propertiesToShow = getPropertiesToShow(mode, shareCommonMaterialSettingsFields);

  const lumbers = componentThickness
    ? inventoryLumbers.filter((lumber) => lumberCanBeUsedForComponentThickness(lumber, componentThickness))
    : inventoryLumbers;

  function updateProperty(lumber: Lumber, key: LumberPropertyKey) {
    onSettingsChange({ ...settings, [key]: lumber });
  }

  return (
    <Box>
      {propertiesToShow.map((settingsProperty) => (
        <Box sx={{ m: 2 }} key={settingsProperty}>
          <LumberOption
            disabled={disabled}
            inputSx={{ m: 1 }}
            lumberSelectSx={{
              display: 'flex',
              justifyContent: 'space-between',
              '& > *': { flex: 1 },
            }}
            name={startCase(settingsProperty.replace('Lumber', ''))}
            lumbers={lumbers}
            lumber={settings[settingsProperty]}
            updateMethod={(lumber: Lumber) => updateProperty(lumber, settingsProperty)}
          />
        </Box>
      ))}
    </Box>
  );
}
